/**
 * Formatos aceitos na tela de carga (ADR-033, Req 2 AC2/AC3).
 *
 * 🔴 Fonte ÚNICA: `backend/contracts/formatos.json`, a mesma lista que o M5 usa para
 * detectar o tipo pelo conteúdo e o M2 para conferir os eventos. Antes a tela tinha a
 * própria lista (sem PDF), e TIFF/BMP passavam aqui e paravam no M5. O JSON entra no
 * bundle pelo Vite; o teste `formatos.test.ts` confere a tela com o arquivo.
 */

import contrato from '../../../backend/contracts/formatos.json';

/** Extensões aceitas (minúsculas, sem o ponto): `jpg`, `png`, `pdf`… */
export const EXTENSOES_ACEITAS: ReadonlySet<string> = new Set(Object.keys(contrato.extensoes));

/** Planilhas no formato antigo (`.xls` — ADR-043): a mesma rota do `.xlsx`; o leitor ainda as recusa com o motivo. */
export const EXTENSOES_PLANILHA_LEGADO: readonly string[] = contrato.planilhas.legado;

/**
 * Extensões da planilha de instalações (ADR-040): importada sozinha na carga de pasta — o `.xlsx` e, desde a ADR-043,
 * o `.xls` (a mesma rota da conferência no servidor).
 */
export const EXTENSOES_PLANILHA: readonly string[] = [...contrato.planilhas.extensoes, ...EXTENSOES_PLANILHA_LEGADO];

/** Planilhas que ficam fora da carga (CSV — salve como .xlsx). */
export const PLANILHAS_FORA_DO_FORMATO: ReadonlySet<string> = new Set(contrato.planilhas.foraDoFormato);

/** Contêineres que o M5 extrai (ADR-043): ZIP, RAR, 7Z e DOCX — decididos pelo conteúdo, não pela extensão. */
export const CONTEINERES: readonly string[] = contrato.conteineres;

/**
 * Valor do `accept` do seletor de arquivos da carga: ".jpg,.jpeg,…,.pdf,.xlsx,.xls,.zip,.rar,.7z,.docx", na ordem do
 * contrato (spec `seletor-de-arquivos-na-carga`, Req 1 AC2; a planilha desde a ADR-040; o `.xls` e os compactados desde
 * a ADR-043 — a extensão usual de cada contêiner é o nome do tipo em minúsculas). Só extensões: o seletor do Windows
 * vira o filtro "Arquivos personalizados" e o navegador compara sem diferenciar maiúsculas. É só o filtro inicial — a
 * tela aceita qualquer arquivo (o arrastar e a pasta não filtram; no seletor, "Todos os arquivos") e o tipo é decidido
 * pelo conteúdo no M5.
 */
export const ACCEPT_SELETOR_ARQUIVOS: string = [
  ...Object.keys(contrato.extensoes),
  ...EXTENSOES_PLANILHA,
  ...CONTEINERES.map((t) => t.toLowerCase()),
]
  .map((ext) => `.${ext}`)
  .join(',');

/** A extensão como está no nome (sem o ponto), ou vazia. */
function extensaoCrua(nome: string): string {
  const idx = nome.lastIndexOf('.');
  return idx <= 0 || idx === nome.length - 1 ? '' : nome.slice(idx + 1);
}

/**
 * O arquivo é a planilha de instalações (ADR-040)? Pela extensão em minúsculas OU maiúsculas exatas — a mesma regra
 * da conferência no servidor (o consumidor só pode ler esses sufixos do acervo). `.Xlsx` fica fora, com o motivo.
 */
export function ehPlanilha(nome: string): boolean {
  const ext = extensaoCrua(nome);
  return EXTENSOES_PLANILHA.some((e) => ext === e || ext === e.toUpperCase());
}

/** A planilha é do formato antigo (`.xls`)? Vai junto, mas o leitor ainda a recusa (ADR-043). */
export function ehPlanilhaLegado(nome: string): boolean {
  const ext = extensaoCrua(nome);
  return EXTENSOES_PLANILHA_LEGADO.some((e) => ext === e || ext === e.toUpperCase());
}

/**
 * Por que o arquivo fica fora da carga (o texto da revisão), ou `null` quando não fica: desde a ADR-043 a tela aceita
 * qualquer arquivo (o tipo é decidido pelo conteúdo); ficam fora só as planilhas que não seriam importadas — o CSV e a
 * extensão de caixa mista (a rota do servidor só reconhece minúsculas ou maiúsculas exatas). O limite de 5 GB é
 * conferido pelo tamanho (`prepararArquivos`).
 */
export function motivoForaDaCarga(nome: string): string | null {
  const ext = extensaoCrua(nome).toLowerCase();
  if (PLANILHAS_FORA_DO_FORMATO.has(ext)) return 'Planilha em CSV — salve como .xlsx';
  if (EXTENSOES_PLANILHA.includes(ext) && !ehPlanilha(nome)) {
    return `Planilha com a extensão em maiúsculas e minúsculas — renomeie para .${ext}`;
  }
  return null;
}

/** O texto da revisão para a planilha antiga que vai junto (ADR-043). */
export const AVISO_PLANILHA_LEGADO = 'Planilha no formato antigo (.xls) — vai junto, mas ainda não é lida; salve como .xlsx';

/** Tipos de arquivo que podem ter várias páginas — cada página vira um documento. */
export const TIPOS_MULTIPAGINA: readonly string[] = contrato.multipagina;

/** Páginas processadas por arquivo; as seguintes ficam fora (registradas no relatório). */
export const LIMITE_PAGINAS: number = contrato.limitePaginas;

/** Limite por arquivo da carga, em bytes (o PUT único do S3 — 5 GB; ADR-043). */
export const LIMITE_BYTES_ARQUIVO: number = contrato.limiteBytesArquivo;

/** O texto da revisão para o arquivo acima do limite (ADR-043): "Acima de 5 GB — o limite por arquivo". */
export const MOTIVO_ACIMA_DO_LIMITE = `Acima de ${LIMITE_BYTES_ARQUIVO / 1024 ** 3} GB — o limite por arquivo`;

const SISTEMA_NOMES = new Set(contrato.sistema.nomes.map((n) => n.toLowerCase()));
const SISTEMA_EXTENSOES = new Set(contrato.sistema.extensoes.map((n) => n.toLowerCase()));
const SISTEMA_PREFIXOS: readonly string[] = contrato.sistema.prefixos.map((n) => n.toLowerCase());

/**
 * O arquivo é de sistema (atalho, miniatura, configuração do sistema operacional) e fica fora do acervo (ADR-043)?
 * Pelo nome do arquivo (sem a pasta): o nome exato, a extensão ou o prefixo, sem diferenciar maiúsculas — a mesma regra
 * do M5 (`contrato_formatos.eh_arquivo_de_sistema`).
 */
export function ehArquivoDeSistema(caminho: string): boolean {
  const base = (caminho.replace(/\\/g, '/').split('/').pop() ?? '').toLowerCase();
  if (SISTEMA_NOMES.has(base) || SISTEMA_PREFIXOS.some((p) => base.startsWith(p))) return true;
  const ponto = base.lastIndexOf('.');
  return ponto > 0 && SISTEMA_EXTENSOES.has(base.slice(ponto + 1));
}
